import { useRef, useState, useEffect } from "react";
import { FaPhoneAlt, FaEnvelope, FaTwitter } from "react-icons/fa";
import { GoLocation } from "react-icons/go";
import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";
import Card from '@mui/material/Card';
import { Button, TextField, Typography, Box } from '@mui/material';

import styles from './Contact.module.scss';

const Contact = () => {
  const form = useRef();
  const recaptchaRef = useRef(null); // Ref for the reCAPTCHA div
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState(null);

  useEffect(() => {
    let intervalId;

    const tryRenderRecaptcha = () => {
       if (typeof window.grecaptcha !== 'undefined' && recaptchaRef.current) {
        clearInterval(intervalId); // Stop polling once grecaptcha is ready

        if (recaptchaWidgetId === null) { 
          console.log("reCAPTCHA API loaded and element found. Attempting to render widget...");
          try {
            const widgetId = window.grecaptcha.render(recaptchaRef.current, {
              'sitekey': process.env.REACT_APP_RECAPTCHA_SITE_KEY,
              'theme': 'light', // or 'dark'
              'callback': (response) => {
                console.log('reCAPTCHA response:', response);
              },
              'error-callback': (error) => {
                console.error('reCAPTCHA rendering error occurred:', error);
                if (widgetId !== null) {
                  window.grecaptcha.reset(widgetId);
                }
              },
              'expired-callback': () => {
                console.warn('reCAPTCHA response expired. Resetting widget.');
                if (widgetId !== null) {
                  window.grecaptcha.reset(widgetId);
                }
              }
            });
            setRecaptchaWidgetId(widgetId);
            console.log("reCAPTCHA widget rendered with ID:", widgetId);
          } catch (e) {
            console.error("Error calling grecaptcha.render:", e);
            // If rendering fails, reset widgetId so it can retry or show loading
            setRecaptchaWidgetId(null); 
          }
        }
      } else {
        console.log("Waiting for reCAPTCHA API or element...", { grecaptchaLoaded: typeof window.grecaptcha !== 'undefined', elementReady: !!recaptchaRef.current });
      }
    };

    intervalId = setInterval(tryRenderRecaptcha, 200); // Check every 200ms
   
    return () => {
      clearInterval(intervalId); 
     
    };
  }, [recaptchaWidgetId]); // Dependency on recaptchaWidgetId

  const sendEmail = (e) => {
    e.preventDefault();

    if (recaptchaWidgetId === null || typeof window.grecaptcha === 'undefined') {
      toast.error("reCAPTCHA widget is not ready. Please try again.");
      console.error("reCAPTCHA API not loaded or widget not rendered (recaptchaWidgetId is null).");
      return;
    }

    const recaptchaResponse = window.grecaptcha.getResponse(recaptchaWidgetId);

    if (!recaptchaResponse) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    emailjs
      .sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        "template_nmklatq",
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
        { "g-recaptcha-response": recaptchaResponse }
      )
      .then(
        (result) => {
          toast.success("Message sent successfully!");
          e.target.reset();
          if (recaptchaWidgetId !== null) {
            window.grecaptcha.reset(recaptchaWidgetId);
          }
          window.gtag('event', 'contact_form_submission', {
            event_category: 'Contact',
            event_label: 'Email Submission',
            value: 1,
          });
          alert("Your message has been sent, and the event has been logged in Google Analytics.");
        },
        (error) => {
          console.error("EmailJS Error:", error);
          toast.error("Failed to send message. Please try again.");
          if (recaptchaWidgetId !== null) {
            window.grecaptcha.reset(recaptchaWidgetId);
          }
        }
      );
  };

  return (
    <section className={styles.contactSection}>
      <div className={styles.container}>
        <Typography variant="h4" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Contact Us
        </Typography>
        <Box className={styles.contentWrapper}>
          <Card className={styles.formCard}>
            <Typography variant="h5" component="h3" gutterBottom>Send us a Message</Typography>
            <form ref={form} onSubmit={sendEmail}>
              <TextField label="Name" type="text" name="user_name" required fullWidth margin="normal" variant="outlined" />
              <TextField label="Email" type="email" name="user_email" required fullWidth margin="normal" variant="outlined" />
              <TextField label="Subject" type="text" name="subject" required fullWidth margin="normal" variant="outlined" />
              <TextField name="message" label="Message" multiline rows={5} fullWidth margin="normal" variant="outlined" />

            {recaptchaWidgetId === null && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>Loading reCAPTCHA...</Typography>
              )}
              <div
                ref={recaptchaRef} 
                style={{ marginTop: '16px', marginBottom: '16px' }}
              >
              </div>
              <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                Send Message
              </Button>
            </form>
            </Card>
         <Card className={styles.detailsCard}>
            <Typography variant="h5" component="h3" gutterBottom>Our Contact Information</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>Fill the form or contact us via other channels listed below</Typography>
            <Box className={styles.contactDetailsList}>
              <Box className={styles.detailItem}>
                <FaPhoneAlt />
                <Typography variant="body1" sx={{ ml: 1 }}>+216 58668613</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <FaEnvelope />
                <Typography variant="body1" sx={{ ml: 1 }}>support@ai.com</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <GoLocation />
                <Typography variant="body1" sx={{ ml: 1 }}>Gafsa, Tunisia</Typography>
              </Box>
              <Box className={styles.detailItem}>
                <FaTwitter />
                <Typography variant="body1" sx={{ ml: 1 }}>@HaninEvan</Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      </div>
    </section>
  );
};

export default Contact;
